import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Description as QuotationIcon,
  ArrowBack as BackIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import ReportDataGrid from '../components/ReportDataGrid';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/apiService';
import image from '../assets/riyal.png';
import { formatDate } from '../utils/dateUtils';


const QuotationReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    status: '',
    converted: '',
  });

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);

      const params = {};
      if (filters.startDate) {
        params.startDate = filters.startDate.toISOString();
      }
      if (filters.endDate) {
        params.endDate = filters.endDate.toISOString();
      }
      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.converted) {
        params.converted = filters.converted;
      }

      const response = await api.get('/reports/quotation-report', { params });

      console.log('Quotation Report Response:', response);
      console.log('Response Data:', response.data);

      if (response.data && response.data.success) {
        const apiData = response.data;

        // The data already has 'id' field from backend, but ensure it's set
        const quotationsWithId = (apiData.data || []).map((quotation) => ({
          ...quotation,
          // Backend already provides 'id', but we can use quotationNo as fallback
          id: quotation.id || quotation.quotationNo,
        }));

        console.log('Formatted Quotations:', quotationsWithId);
        console.log('Summary:', apiData.summary);

        setReportData(quotationsWithId);
        setSummary(apiData.summary || null);
      }
    } catch (err) {
      console.error('Error fetching quotation report:', err);
      alert('Failed to fetch quotation report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  // Define columns for the data grid
  const columns = [
    {
      field: 'quotationNo',
      headerName: 'Quotation #',
      flex: 1,
      minWidth: 130
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      minWidth: 120,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: 'customer',
      headerName: 'Customer',
      flex: 1.5,
      minWidth: 180
    },
    {
      field: 'validUntil',
      headerName: 'Valid Until',
      flex: 1,
      minWidth: 120,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={
            params.value === 'accepted' ? 'success' :
              params.value === 'sent' ? 'info' :
                params.value === 'rejected' ? 'error' :
                  params.value === 'expired' ? 'warning' :
                    'default'
          }
          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
        />
      )
    },
    // {
    //   field: 'amount',
    //   headerName: 'Amount',
    //   flex: 1,
    //   minWidth: 120,
    //   type: 'number',
    //   align: 'right',
    //   headerAlign: 'right',
    //   valueFormatter: ({ value }) => `SAR ${value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`
    // },

    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '6px',
            width: '100%',
          }}
        >
          <img
            src={image}
            alt="Riyal"
            style={{
              width: 18,
              height: 18,
              objectFit: 'contain',
            }}
          />
          <span>
            {params.value?.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || '0.00'}
          </span>
        </div>
      ),
    },
    {
      field: 'vat',
      headerName: 'VAT',
      flex: 0.8,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '6px',
            width: '100%',
          }}
        >
          <img
            src={image}
            alt="Riyal"
            style={{
              width: 18,
              height: 18,
              objectFit: 'contain',
            }}
          />
          <span>
            {params.value?.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || '0.00'}
          </span>
        </div>
      ),
    },
    // {
    //   field: 'total',
    //   headerName: 'Total',
    //   flex: 1,
    //   minWidth: 130,
    //   type: 'number',
    //   align: 'right',
    //   headerAlign: 'right',
    //   valueFormatter: ({ value }) => `SAR ${value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`,
    //   cellClassName: 'font-weight-bold'
    // },
    {
      field: 'total',
      headerName: 'Total',
      flex: 1,
      minWidth: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      cellClassName: 'font-weight-bold',
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '6px',
            width: '100%',
            fontWeight: 'bold',
          }}
        >
          <img
            src={image} // import image from '../assets/riyal.png';
            alt="Riyal"
            style={{
              width: 18,
              height: 18,
              objectFit: 'contain',
            }}
          />
          <span>
            {params.value?.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || '0.00'}
          </span>
        </div>
      ),
    },
    {
      field: 'convertedToInvoice',
      headerName: 'Converted',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        params.value ? (
          <Chip
            label={params.row.invoiceNo || 'Yes'}
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          />
        ) : (
          <Chip
            label="No"
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          />
        )
      )
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
        <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={() => navigate('/reports')}
                sx={{ mr: 1, bgcolor: 'rgba(67, 97, 238, 0.1)' }}
              >
                <BackIcon />
              </IconButton>
              <Box>
                <Typography variant="h5" component="h1" fontWeight="bold">
                  Quotation Report
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  View and analyze all quotations with detailed insights
                </Typography>
              </Box>
            </Box>
            <Box>
              <IconButton
                sx={{
                  color: '#2196f3',
                  border: '1px solid #2196f3',
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.08)'
                  }
                }}
              >
                <AssessmentIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Filters */}
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 0,
                        }
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 0,
                        }
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Conversion Status</InputLabel>
                  <Select
                    value={filters.converted}
                    onChange={(e) => handleFilterChange('converted', e.target.value)}
                    label="Conversion Status"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: 0,
                      }
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Converted</MenuItem>
                    <MenuItem value="false">Not Converted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleApplyFilters}
                  sx={{ height: '40px', borderRadius: 0 }}
                >
                  Apply Filters
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Summary Cards */}
          {summary && (
            <Grid container spacing={1} sx={{ mb: 0.5 }}>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0,
                    borderLeft: '4px solid #4361ee',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        bgcolor: '#4361ee15',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                      }}
                    >
                      <QuotationIcon sx={{ color: '#4361ee', fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Quotations
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        {summary.totalQuotations || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0,
                    borderLeft: '4px solid #7c4dff',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        bgcolor: '#7c4dff15',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                      }}
                    >
                      <MoneyIcon sx={{ color: '#7c4dff', fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Amount
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        <img src={image} alt="" style={{ width: '20px', height: '20px', marginRight: '5px' }} /> {summary.totalValue?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0,
                    borderLeft: '4px solid #ff9800',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        bgcolor: '#ff980015',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                      }}
                    >
                      <TrendingUpIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total VAT
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        <img src={image} alt="" style={{ width: '20px', height: '20px', marginRight: '5px' }} /> {summary.totalVAT?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0,
                    borderLeft: '4px solid #4caf50',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        bgcolor: '#4caf5015',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                      }}
                    >
                      <ReceiptIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Converted
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        {summary.converted || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0,
                    borderLeft: '4px solid #f44336',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        bgcolor: '#f4433615',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                      }}
                    >
                      <QuotationIcon sx={{ color: '#f44336', fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Not Converted
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        {(summary.totalQuotations - summary.converted) || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Data Grid */}
          <ReportDataGrid
            title="Quotation Report"
            data={reportData}
            columns={columns}
            loading={loading}
            pageSize={25}
            onRefresh={fetchReport}
            exportFileName="quotation-report"
            getRowId={(row) => row.id || row.quotationNumber || Math.random().toString()}
            totals={summary}
            dateRange={{ startDate: filters.startDate, endDate: filters.endDate }}
          />
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default QuotationReport;
